import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { calendarApi } from '../../api/calendar';
import { SegmentedControl } from '../../components/ui';
import { EVENT_TYPE_ICONS } from '../../types/event';
import type { CalendarItem } from '../../types/calendar';

// --- Helpers ---------------------------------------------------------------

const DAY_NAMES_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const MONTH_NAMES = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
const MONTH_NAMES_NOM = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

function getMonday(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d);
  mon.setDate(diff);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function fmt(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return fmt(a) === fmt(b);
}

// --- Week View Component ---------------------------------------------------

interface WeekDayStripProps {
  weekStart: Date;
  selectedDate: Date;
  items: CalendarItem[];
  onSelectDate: (d: Date) => void;
}

const WeekDayStrip: React.FC<WeekDayStripProps> = ({
  weekStart,
  selectedDate,
  items,
  onSelectDate,
}) => {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = new Date();

  // Count items per date
  const dateCounts = useMemo(() => {
    const map = new Map<string, { trainings: number; events: number }>();
    items.forEach((item) => {
      const entry = map.get(item.date) || { trainings: 0, events: 0 };
      // Отменённое занятие тренировкой дня не считаем
      if (item.type === 'training') {
        if (!item.cancelled) entry.trainings++;
      } else entry.events++;
      map.set(item.date, entry);
    });
    return map;
  }, [items]);

  return (
    <div className="flex justify-between px-1">
      {days.map((day) => {
        const dateStr = fmt(day);
        const isSelected = isSameDay(day, selectedDate);
        const isToday = isSameDay(day, today);
        const counts = dateCounts.get(dateStr);

        return (
          <button
            key={dateStr}
            onClick={() => onSelectDate(day)}
            className="flex flex-col items-center gap-1 py-2 px-1.5 rounded-xl transition-colors min-w-[40px]"
            style={{
              backgroundColor: isSelected ? 'var(--accent)' : 'transparent',
            }}
          >
            <span
              className="text-[11px] font-medium"
              style={{
                color: isSelected ? '#FFFFFF' : 'var(--text-hint)',
              }}
            >
              {DAY_NAMES_SHORT[day.getDay()]}
            </span>
            <span
              className="text-[16px] font-semibold w-8 h-8 flex items-center justify-center rounded-full"
              style={{
                color: isSelected
                  ? '#FFFFFF'
                  : isToday
                    ? 'var(--accent)'
                    : 'var(--text-primary)',
                backgroundColor: isToday && !isSelected ? 'var(--accent-light, rgba(245,124,0,0.12))' : 'transparent',
              }}
            >
              {day.getDate()}
            </span>
            {/* Dots */}
            <div className="flex gap-0.5 h-2">
              {counts?.trainings ? (
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: isSelected ? '#FFFFFF' : 'var(--accent)' }}
                />
              ) : null}
              {counts?.events ? (
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: isSelected ? '#FFE0B2' : '#1976D2' }}
                />
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
};

// --- Month Grid Component --------------------------------------------------

interface MonthGridProps {
  year: number;
  month: number; // 0-based
  selectedDate: Date;
  items: CalendarItem[];
  onSelectDate: (d: Date) => void;
}

const MonthGrid: React.FC<MonthGridProps> = ({
  year,
  month,
  selectedDate,
  items,
  onSelectDate,
}) => {
  const today = new Date();

  // Count items per date
  const dateCounts = useMemo(() => {
    const map = new Map<string, { trainings: number; events: number }>();
    items.forEach((item) => {
      const entry = map.get(item.date) || { trainings: 0, events: 0 };
      // Отменённое занятие тренировкой дня не считаем
      if (item.type === 'training') {
        if (!item.cancelled) entry.trainings++;
      } else entry.events++;
      map.set(item.date, entry);
    });
    return map;
  }, [items]);

  // Build grid: 7 columns, Mon-Sun
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Day of week for first day (Mon=0 ... Sun=6)
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const totalDays = lastDay.getDate();
  const cells: (Date | null)[] = [];

  // Empty cells before first day
  for (let i = 0; i < startOffset; i++) cells.push(null);
  // Actual days
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      {/* Header row: Пн-Вс */}
      <div className="grid grid-cols-7 mb-1">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d) => (
          <div
            key={d}
            className="text-center text-[11px] font-medium py-1"
            style={{ color: 'var(--text-hint)' }}
          >
            {d}
          </div>
        ))}
      </div>
      {/* Days grid */}
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          if (!cell) {
            return <div key={`empty-${idx}`} className="h-11" />;
          }

          const dateStr = fmt(cell);
          const isSelected = isSameDay(cell, selectedDate);
          const isToday = isSameDay(cell, today);
          const counts = dateCounts.get(dateStr);

          return (
            <button
              key={dateStr}
              onClick={() => onSelectDate(cell)}
              className="flex flex-col items-center justify-center h-11 rounded-lg transition-colors"
              style={{
                backgroundColor: isSelected ? 'var(--accent)' : 'transparent',
              }}
            >
              <span
                className="text-[14px] font-medium"
                style={{
                  color: isSelected
                    ? '#FFFFFF'
                    : isToday
                      ? 'var(--accent)'
                      : 'var(--text-primary)',
                }}
              >
                {cell.getDate()}
              </span>
              <div className="flex gap-0.5 h-1.5">
                {counts?.trainings ? (
                  <span
                    className="w-1 h-1 rounded-full"
                    style={{ backgroundColor: isSelected ? '#FFFFFF' : 'var(--accent)' }}
                  />
                ) : null}
                {counts?.events ? (
                  <span
                    className="w-1 h-1 rounded-full"
                    style={{ backgroundColor: isSelected ? '#FFE0B2' : '#1976D2' }}
                  />
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --- Item Card Component ---------------------------------------------------

const CalendarItemCard: React.FC<{
  item: CalendarItem;
  onTap: () => void;
}> = ({ item, onTap }) => {
  const isTraining = item.type === 'training';
  const icon = isTraining
    ? '🟠'
    : EVENT_TYPE_ICONS[item.event_type as keyof typeof EVENT_TYPE_ICONS] || '📅';
  // Отменённое занятие оставляем на месте, но зачёркнутым: тренеру важно
  // видеть, что в этот день тренировки не будет, а не гадать, куда она делась
  const tag = item.cancelled ? 'Отменена' : item.extra ? 'Разовая' : null;
  const strike = item.cancelled ? 'line-through' : undefined;

  return (
    <button
      onClick={onTap}
      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors"
      style={{ backgroundColor: 'var(--bg-secondary)', opacity: item.cancelled ? 0.6 : 1 }}
    >
      <span className="text-lg flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="text-[14px] font-semibold"
            style={{ color: isTraining ? 'var(--accent)' : '#1976D2', textDecoration: strike }}
          >
            {item.start_time}
            {item.end_time ? `–${item.end_time}` : ''}
          </span>
          <span
            className="text-[14px] font-medium truncate"
            style={{ color: 'var(--text-primary)', textDecoration: strike }}
          >
            {item.title}
          </span>
          {tag && (
            <span
              className="text-[11px] font-medium px-1.5 py-0.5 rounded-md flex-shrink-0"
              style={{ color: 'var(--text-secondary)', border: '1px solid var(--text-hint)' }}
            >
              {tag}
            </span>
          )}
        </div>
        {item.location && (
          <div className="flex items-center gap-1 mt-0.5">
            <MapPin size={11} style={{ color: 'var(--text-hint)' }} />
            <span
              className="text-[12px] truncate"
              style={{ color: 'var(--text-hint)' }}
            >
              {item.location}
            </span>
          </div>
        )}
      </div>
    </button>
  );
};

// --- Main Screen -----------------------------------------------------------

export const CalendarScreen: React.FC = () => {
  const { currentAssistantId } = useAuthStore();
  const { pushScreen, popScreen } = useTabNavigationStore();
  const params = useTabNavigationStore((s) => {
    const stack = s.tabStacks[s.activeTab];
    return stack[stack.length - 1]?.params || {};
  });

  // View mode
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  // Filter
  const [itemFilter, setItemFilter] = useState<'all' | 'trainings' | 'events'>('all');

  // Current reference date
  const [refDate, setRefDate] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(() => new Date());

  // Coach/group filter from params
  const coachId = params.coachId as string | undefined;
  const groupId = params.groupId as string | undefined;

  // Compute query range
  const { from, to } = useMemo(() => {
    if (viewMode === 'week') {
      const weekStart = getMonday(refDate);
      return { from: fmt(weekStart), to: fmt(addDays(weekStart, 7)) };
    }
    // Month view: fetch full month
    const y = refDate.getFullYear();
    const m = refDate.getMonth();
    return {
      from: fmt(new Date(y, m, 1)),
      to: fmt(new Date(y, m + 1, 1)),
    };
  }, [viewMode, refDate]);

  // Fetch calendar data
  const { data, isLoading } = useQuery({
    queryKey: ['calendar', currentAssistantId, from, to, itemFilter, coachId, groupId],
    queryFn: () =>
      calendarApi.getCalendar(currentAssistantId!, {
        from,
        to,
        type: itemFilter,
        coach_id: coachId,
        group_id: groupId,
      }),
    enabled: !!currentAssistantId,
  });

  const items = data?.items || [];

  // Items for selected date
  const selectedDateStr = fmt(selectedDate);
  const dayItems = useMemo(
    () => items.filter((item) => item.date === selectedDateStr),
    [items, selectedDateStr],
  );

  // Navigation
  const handlePrev = () => {
    if (viewMode === 'week') {
      setRefDate((d) => addDays(d, -7));
    } else {
      setRefDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'week') {
      setRefDate((d) => addDays(d, 7));
    } else {
      setRefDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    }
  };

  const handleItemTap = (item: CalendarItem) => {
    if (item.type === 'training' && item.group_id) {
      pushScreen('group-detail', { groupId: item.group_id });
    } else if (item.type === 'event' && item.event_id) {
      pushScreen('event-detail', { eventId: item.event_id });
    }
  };

  // Format header
  const headerText = useMemo(() => {
    if (viewMode === 'week') {
      const weekStart = getMonday(refDate);
      const weekEnd = addDays(weekStart, 6);
      const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
      if (sameMonth) {
        return `${weekStart.getDate()} – ${weekEnd.getDate()} ${MONTH_NAMES[weekStart.getMonth()]} ${weekStart.getFullYear()}`;
      }
      return `${weekStart.getDate()} ${MONTH_NAMES[weekStart.getMonth()]} – ${weekEnd.getDate()} ${MONTH_NAMES[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`;
    }
    return `${MONTH_NAMES_NOM[refDate.getMonth()]} ${refDate.getFullYear()}`;
  }, [viewMode, refDate]);

  // Format selected day header
  const selectedDayHeader = useMemo(() => {
    const d = selectedDate;
    const dayNames = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
    return `${dayNames[d.getDay()]}, ${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`;
  }, [selectedDate]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className="flex-shrink-0 px-4 pt-3 pb-2"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        {/* Back button + title */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={popScreen}
            className="p-1 -ml-1"
            style={{ color: 'var(--accent)' }}
          >
            <ChevronLeft size={24} />
          </button>
          <h1
            className="text-[18px] font-bold flex-1"
            style={{ color: 'var(--text-primary)' }}
          >
            Календарь
          </h1>
        </div>

        {/* View mode toggle */}
        <SegmentedControl
          segments={[
            { value: 'week', label: 'Неделя' },
            { value: 'month', label: 'Месяц' },
          ]}
          value={viewMode}
          onChange={(v) => setViewMode(v as 'week' | 'month')}
        />

        {/* Date navigation */}
        <div className="flex items-center justify-between mt-3">
          <button onClick={handlePrev} className="p-1" style={{ color: 'var(--accent)' }}>
            <ChevronLeft size={20} />
          </button>
          <span
            className="text-[14px] font-semibold"
            style={{ color: 'var(--text-primary)' }}
          >
            {headerText}
          </span>
          <button onClick={handleNext} className="p-1" style={{ color: 'var(--accent)' }}>
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Calendar grid/strip */}
        <div className="mt-2">
          {viewMode === 'week' ? (
            <WeekDayStrip
              weekStart={getMonday(refDate)}
              selectedDate={selectedDate}
              items={items}
              onSelectDate={setSelectedDate}
            />
          ) : (
            <MonthGrid
              year={refDate.getFullYear()}
              month={refDate.getMonth()}
              selectedDate={selectedDate}
              items={items}
              onSelectDate={setSelectedDate}
            />
          )}
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 mt-3">
          {(['all', 'trainings', 'events'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setItemFilter(f)}
              className="px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors"
              style={{
                backgroundColor: itemFilter === f ? 'var(--accent)' : 'var(--bg-secondary)',
                color: itemFilter === f ? '#FFFFFF' : 'var(--text-primary)',
              }}
            >
              {f === 'all' ? 'Все' : f === 'trainings' ? 'Тренировки' : 'События'}
            </button>
          ))}
        </div>
      </div>

      {/* Items list */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
        {isLoading ? (
          <div className="animate-pulse space-y-3 mt-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-14 rounded-xl"
                style={{ backgroundColor: 'var(--bg-secondary)' }}
              />
            ))}
          </div>
        ) : dayItems.length === 0 ? (
          <div className="text-center mt-8">
            <p
              className="text-[14px]"
              style={{ color: 'var(--text-hint)' }}
            >
              Нет событий на этот день
            </p>
          </div>
        ) : (
          <>
            {/* Day header */}
            <h3
              className="text-[13px] font-semibold mt-3 mb-2"
              style={{ color: 'var(--text-hint)' }}
            >
              {selectedDayHeader}
            </h3>
            <div className="space-y-2">
              {dayItems.map((item, idx) => (
                <CalendarItemCard
                  key={`${item.date}-${item.start_time}-${item.title}-${idx}`}
                  item={item}
                  onTap={() => handleItemTap(item)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
